import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LoggerService } from './logger.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { MessageSet } from '../models/message-set.model';
import {
  getCurrentCustomer,
  UserState,
} from 'src/app/login/state/user.reducers';
import { Customer } from '../models/customer.model';
import { Store } from '@ngrx/store';
import { ApiResponse } from '../models/api-response';

@Injectable({
  providedIn: 'root',
})
export class MessageSetTemplateService {
  endpointUrl: string = environment.apiUrl + '/template-message-set';

  constructor(
    private logger: LoggerService,
    private http: HttpClient,
    private store: Store<UserState>
  ) {}

  /**
   * Get all template message sets for the current customer
   */
  getMessageSetTemplates(): Observable<MessageSet[]> {
    const params = this.getCustomerParams();
    return this.http
      .get<ApiResponse<MessageSet>>(this.endpointUrl, { params })
      .pipe(
        map((response) => response.results || []),
        catchError(this.handleError)
      );
  }

  /**
   * Get paginated template message sets for the current customer
   */
  getMessageSetTemplatesPaginated(url?: string): Observable<ApiResponse<MessageSet>> {
    const urlToCall = url || this.endpointUrl;
    const params = url ? new HttpParams() : this.getCustomerParams();
    return this.http
      .get<ApiResponse<MessageSet>>(urlToCall, { params })
      .pipe(catchError(this.handleError));
  }

  /**
   * Get a single template message set by ID
   */
  getMessageSetTemplate(id: number): Observable<MessageSet> {
    return this.http
      .get<MessageSet>(`${this.endpointUrl}/${id}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Create a new template message set
   */
  createMessageSetTemplate(messageSet: MessageSet, ownerId?: number): Observable<MessageSet> {
    const payload = this.preparePayload(messageSet, ownerId);
    return this.http
      .post<MessageSet>(this.endpointUrl, payload)
      .pipe(catchError(this.handleError));
  }

  /**
   * Update an existing template message set
   */
  updateMessageSetTemplate(messageSet: MessageSet): Observable<MessageSet> {
    const payload = this.preparePayload(messageSet);
    return this.http
      .put<MessageSet>(`${this.endpointUrl}/${messageSet.id}`, payload)
      .pipe(catchError(this.handleError));
  }

  /**
   * Partially update an existing template message set
   */
  patchMessageSetTemplate(id: number, updates: Partial<MessageSet>): Observable<MessageSet> {
    return this.http
      .patch<MessageSet>(`${this.endpointUrl}/${id}`, updates)
      .pipe(catchError(this.handleError));
  }

  /**
   * Delete a template message set
   */
  deleteMessageSetTemplate(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.endpointUrl}/${id}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Search template message sets
   */
  searchMessageSetTemplates(searchTerm: string): Observable<MessageSet[]> {
    let params = this.getCustomerParams();
    params = params.set('search', searchTerm);
    return this.http
      .get<ApiResponse<MessageSet>>(this.endpointUrl, { params })
      .pipe(
        map((response) => response.results || []),
        catchError(this.handleError)
      );
  }

  /**
   * Get params with current customer ID
   */
  private getCustomerParams(): HttpParams {
    let params = new HttpParams();
    let customer: Customer;

    this.store
      .select(getCurrentCustomer)
      .pipe(take(1))
      .subscribe((c: Customer) => (customer = c));

    if (customer?.id) {
      params = params.set('customer', customer.id.toString());
    }
    return params;
  }

  /**
   * Prepare message set payload for API
   */
  private preparePayload(messageSet: MessageSet, ownerId?: number): any {
    let customer: Customer;
    this.store
      .select(getCurrentCustomer)
      .pipe(take(1))
      .subscribe((c: Customer) => (customer = c));

    const payload: any = {
      ...messageSet,
      customer: customer?.id,
    };

    if (ownerId) {
      payload.owner = ownerId;
    }

    // Convert image/video objects to IDs if needed
    if (messageSet.messages) {
      payload.messages = messageSet.messages.map((message) => ({
        ...message,
        images: message.images?.map((img: any) => (typeof img === 'object' ? img.id : img)),
        videos: message.videos?.map((vid: any) => (typeof vid === 'object' ? vid.id : vid)),
      }));
    }

    return payload;
  }

  private handleError(error: any): Observable<never> {
    const errMsg = error.message
      ? error.message
      : error.status
      ? `${error.status} - ${error.statusText}`
      : 'Server error';
    console.error('MessageSetTemplateService error:', errMsg);
    return throwError(() => new Error(errMsg));
  }
}
